import { useQuery } from '@tanstack/react-query';
import { apiGet } from '../lib/api.js';

/**
 * Two kinds, because the console has two: a group and a URL.
 *
 * Domains are deliberately absent. They are an implementation detail of the
 * schema that the operator no longer navigates by — a URL carries its own
 * hostname — and offering them here would send someone to a screen the rest
 * of the console keeps out of the way.
 */
export type SearchItem = {
  kind: 'group' | 'url';
  id: number;
  label: string;
  sublabel: string;
  href: string;
};

type GroupItem = { id: number; name: string; entryCount: number };

/** One row of `GET /api/urls` — only the fields the palette indexes. */
type UrlItem = {
  entryId: number;
  fullUrl: string;
  label: string;
  groupName: string;
};

/**
 * Scores a candidate against the query.
 *
 * A prefix match outranks a mid-string match, and a label match outranks a
 * sublabel match — so typing "promos" surfaces the URL that starts with it
 * before the one that merely contains it.
 */
function score(item: SearchItem, query: string): number {
  const label = item.label.toLowerCase();
  const sublabel = item.sublabel.toLowerCase();

  if (label.startsWith(query)) return 100 - label.length / 1000;
  const labelIndex = label.indexOf(query);
  if (labelIndex !== -1) return 60 - labelIndex;
  if (sublabel.startsWith(query)) return 40;
  if (sublabel.includes(query)) return 20;
  return -1;
}

export function rankSearch(items: readonly SearchItem[], rawQuery: string): SearchItem[] {
  const query = rawQuery.trim().toLowerCase();
  if (query === '') return [...items];

  return items
    .map((item) => ({ item, rank: score(item, query) }))
    .filter((scored) => scored.rank >= 0)
    .sort((left, right) => right.rank - left.rank)
    .map((scored) => scored.item);
}

/** Builds the palette index from the group listing and the flat URL listing. */
export function buildSearchIndex(
  groups: readonly GroupItem[],
  urls: readonly UrlItem[],
): SearchItem[] {
  return [
    ...groups.map((group) => ({
      kind: 'group' as const,
      id: group.id,
      label: group.name,
      sublabel: `${group.entryCount} URL${group.entryCount === 1 ? '' : 's'}`,
      // The group's URLs, not its domains: that is what opening a group means
      // everywhere else in the console now.
      href: `/urls?group=${group.id}`,
    })),
    ...urls.map((url) => ({
      kind: 'url' as const,
      id: url.entryId,
      // The whole URL, not the bare path: the operator recognises a URL by its
      // hostname, and two groups can easily configure the same path.
      label: url.fullUrl,
      // The operator's own words for the URL are the thing they are most
      // likely to type, so the label is indexed alongside the group.
      sublabel: url.label === '' ? url.groupName : `${url.label} · ${url.groupName}`,
      href: `/urls/${url.entryId}`,
    })),
  ];
}

export function useSearchIndex(): { items: SearchItem[]; isLoading: boolean } {
  /* Both queries reuse the keys the Groups and URLs screens already cache
     under, so the palette is free once either screen has been visited. */
  const groups = useQuery({
    queryKey: ['groups'],
    queryFn: () => apiGet<{ groups: GroupItem[] }>('/api/groups'),
    staleTime: 60_000,
  });

  /*
   * One request for every URL in the install, not one per domain.
   *
   * This used to fan out to `/api/domains/:id/entries` for each domain the
   * liveness report returned — sixty-six requests on a realistic install,
   * fired on every page load, for an index nobody had asked to search yet.
   * `GET /api/urls` is the same data flattened, and it is the exact query the
   * URLs screen already caches.
   */
  const urls = useQuery({
    queryKey: ['urls', 'all'],
    queryFn: () => apiGet<{ urls: UrlItem[] }>('/api/urls'),
    staleTime: 60_000,
  });

  return {
    items: buildSearchIndex(groups.data?.groups ?? [], urls.data?.urls ?? []),
    isLoading: groups.isLoading || urls.isLoading,
  };
}
